import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Stars } from "@/components/landing/Stars";
import { imgUrl } from "@/components/landing/useProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const ReviewsTab = ({ headers }) => {
  const [reviews, setReviews] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/reviews`, { headers }).then((r) => setReviews(r.data)).catch(() => toast.error("Could not load reviews."));
  }, [headers]);

  const remove = async (id) => {
    if (!window.confirm("Delete this review? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers });
      setReviews((l) => l.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch {
      toast.error("Could not delete review.");
    }
  };

  if (reviews === null) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></div>;
  const s = q.trim().toLowerCase();
  const rows = s ? reviews.filter((r) => [r.product_name, r.reviewer, r.email, r.text].some((x) => (x || "").toLowerCase().includes(s))) : reviews;
  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "—";

  return (
    <div data-testid="admin-reviews" className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <p data-testid="reviews-stats" className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#5f5e5a]">{reviews.length} review{reviews.length === 1 ? "" : "s"} · avg {avg} ★ · verified buyers only, published instantly</p>
        <input data-testid="reviews-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, name, email, text" className="h-11 w-full sm:w-80 px-4 rounded-full bg-[#faf9f6] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60" />
      </div>
      {!rows.length ? (
        <p data-testid="reviews-empty" className="mt-16 text-center text-[#5f5e5a] font-mono-d text-[11px] uppercase tracking-[0.2em]">{reviews.length ? "No matches." : "No reviews yet."}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id} data-testid={`admin-review-${r.id}`} className="border border-[#e3e2dc] rounded-2xl bg-[#faf9f6] p-5 flex flex-col sm:flex-row gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Stars value={r.rating} size={13} testId={`admin-review-stars-${r.id}`} />
                  <span className="font-medium text-sm">{r.reviewer}</span>
                  <span className="text-xs text-[#5f5e5a]">{r.email}</span>
                  <span className="font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#5f5e5a] ml-auto">{fmt(r.created_at)}</span>
                </div>
                <p className="font-mono-d text-[10px] uppercase tracking-[0.15em] text-[#ff2a85] mt-2">{r.product_name}{r.color ? ` · ${r.color}` : ""}{r.size ? ` · ${r.size}` : ""}</p>
                <p className="mt-2 text-sm leading-relaxed">{r.text}</p>
                {r.photos?.length > 0 && (
                  <div className="mt-3 flex gap-2">
                    {r.photos.map((p) => <a key={p} href={imgUrl(p)} target="_blank" rel="noreferrer" className="h-16 w-16 rounded-lg overflow-hidden border border-[#e3e2dc]"><img src={imgUrl(p)} alt="" className="h-full w-full object-cover" /></a>)}
                  </div>
                )}
              </div>
              <button data-testid={`admin-review-delete-${r.id}`} onClick={() => remove(r.id)} className="self-start inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#e3e2dc] hover:border-[#ff2a85] hover:text-[#ff2a85] font-mono-d text-[9px] uppercase tracking-[0.2em] transition-colors">
                <Trash2 size={12} /> Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
